/**
 * Pure, framework-free visibility engine for nested annotation properties.
 *
 * A property is "visible" when:
 *   - it has no parent (top-level), OR
 *   - its parent is itself visible, AND
 *   - the parent currently holds at least one value that satisfies
 *     the child's `trigger_condition`.
 *
 * Visibility governs both rendering in the workview and required-field
 * validation: an invisible required child is not counted as missing.
 *
 * The engine is the frontend twin of the backend `GetRequiredPropertiesPresence`
 * logic and of the export visibility filter. Keeping the three implementations
 * in sync is a hard release gate.
 */
import type {
  TeamProperty,
  TeamPropertyAudioValue,
  TeamPropertyTextValue,
  TriggerCondition,
} from '@/store/types/PropertyTypes'
import {
  MAX_PROPERTY_NESTING_DEPTH,
  isTeamPropertyAudioValue,
  isTeamPropertyTextValue,
} from '@/store/types/PropertyTypes'

/**
 * Per-property selected values, as stored on an annotation for one frame
 * (or for the "global" annotation-granularity bucket) and as returned by the
 * item-level properties endpoint.
 *
 * The value is either an array of `property_value_id` strings (for select
 * properties) or a single-element array with a `{ text }` object (for text
 * properties).
 */
export type PropertyValueLookup = Record<
  string,
  readonly (string | TeamPropertyTextValue)[] | null | undefined
>

/**
 * Narrow a selected value to a text value.
 */
const isTextValue = (value: string | TeamPropertyTextValue): value is TeamPropertyTextValue =>
  isTeamPropertyTextValue(value)

/**
 * Returns true when the parent's current values contain any data that the
 * engine would consider "present". Whitespace-only text is treated as empty,
 * matching the backend's definition.
 */
const hasAnyValue = (
  values: readonly (string | TeamPropertyTextValue)[] | null | undefined,
): boolean => {
  if (!values || values.length === 0) return false
  return values.some((v) => {
    if (isTextValue(v)) return v.text.trim().length > 0
    return typeof v === 'string' && v.length > 0
  })
}

/**
 * Returns true when the parent's current values include any of the ids
 * listed in the trigger condition.
 */
const matchesValueIds = (
  values: readonly (string | TeamPropertyTextValue)[] | null | undefined,
  ids: readonly string[],
): boolean => {
  if (!values || values.length === 0 || ids.length === 0) return false
  return values.some((v) => !isTextValue(v) && ids.includes(v))
}

/**
 * Evaluates a single trigger condition against a parent's current values.
 */
export const matchesTrigger = (
  trigger: TriggerCondition | null | undefined,
  parentValues: readonly (string | TeamPropertyTextValue)[] | null | undefined,
): boolean => {
  if (!trigger) return false
  if (trigger.type === 'any_value') return hasAnyValue(parentValues)
  if (trigger.type === 'value_match') {
    return matchesValueIds(parentValues, trigger.property_value_ids)
  }
  return false
}

type PropertyLike = Pick<TeamProperty, 'id' | 'parent_property_id' | 'trigger_condition'>

/**
 * Build a fast id -> property index for the given collection. Accepts any
 * shape that has `id`, `parent_property_id`, and `trigger_condition` so it can
 * be reused for both `TeamProperty` and the `Property` view used in the class
 * editor.
 */
const indexProperties = <T extends PropertyLike>(properties: readonly T[]): Map<string, T> => {
  const map = new Map<string, T>()
  for (const p of properties) map.set(p.id, p)
  return map
}

/**
 * Determines whether `property` is visible given the current `values` map and
 * the full property set. The walk is bounded by `MAX_PROPERTY_NESTING_DEPTH`
 * to defend against malformed data that would otherwise cause an infinite
 * loop; bounded depth is enforced at creation time on the backend.
 */
export const isPropertyVisible = <T extends PropertyLike>(
  property: T,
  values: PropertyValueLookup,
  properties: readonly T[] | Map<string, T>,
): boolean => {
  const index = properties instanceof Map ? properties : indexProperties(properties)

  let current: T | undefined = property
  let hops = 0
  while (current && current.parent_property_id) {
    if (hops++ >= MAX_PROPERTY_NESTING_DEPTH) return false

    const parent = index.get(current.parent_property_id)
    if (!parent) return false

    if (!matchesTrigger(current.trigger_condition, values[parent.id])) {
      return false
    }

    current = parent
  }
  return true
}

/**
 * Filters a collection of properties down to those currently visible.
 * Order is preserved.
 */
export const filterVisibleProperties = <T extends PropertyLike>(
  properties: readonly T[],
  values: PropertyValueLookup,
): T[] => {
  const index = indexProperties(properties)
  return properties.filter((p) => isPropertyVisible(p, values, index))
}

/**
 * Computes the ancestor chain for a property, from its immediate parent up
 * to the root. Returns an empty array when the property is top-level. The
 * returned list is ordered from parent to root.
 */
export const getAncestors = <T extends PropertyLike>(
  property: T,
  properties: readonly T[] | Map<string, T>,
): T[] => {
  const index = properties instanceof Map ? properties : indexProperties(properties)
  const ancestors: T[] = []
  let current: T | undefined = property
  let hops = 0
  while (current && current.parent_property_id) {
    if (hops++ >= MAX_PROPERTY_NESTING_DEPTH) break
    const parent = index.get(current.parent_property_id)
    if (!parent) break
    ancestors.push(parent)
    current = parent
  }
  return ancestors
}

/**
 * Returns true when `property.id` — or the id of any of its ancestors — is in
 * `allowedIds`. Used to inherit Stage-Specific Properties allow-list membership
 * down the nested-property subtree, since SSP only whitelists top-level
 * properties; allowing the root must implicitly allow its whole subtree.
 *
 * Callers are responsible for first gating on `isPropertyFilterApplied`.
 */
export const isPropertyAllowedByAncestry = <T extends PropertyLike>(
  property: T,
  properties: readonly T[] | Map<string, T>,
  allowedIds: ReadonlySet<string>,
): boolean => {
  if (allowedIds.has(property.id)) return true
  return getAncestors(property, properties).some((ancestor) => allowedIds.has(ancestor.id))
}

/**
 * Returns the 0-indexed nesting depth of a property, matching the backend's
 * `compute_depth`: top-level properties have depth 0, and the deepest allowed
 * child has depth `MAX_PROPERTY_NESTING_DEPTH`. Callers can compare the result
 * directly against `MAX_PROPERTY_NESTING_DEPTH` without any offset.
 */
export const getDepth = <T extends PropertyLike>(
  property: T,
  properties: readonly T[] | Map<string, T>,
): number => getAncestors(property, properties).length

/**
 * Returns the ids of every descendant of `propertyId` — children, grandchildren,
 * and so on — as a flat set. Traversal is bounded by `MAX_PROPERTY_NESTING_DEPTH`
 * to defend against malformed/cyclical input.
 *
 * Used to keep the frontend property cache consistent with the backend's
 * cascading soft-delete semantics: when a property or a triggering property
 * value is removed, the backend also removes the entire subtree rooted at
 * each affected child.
 */
export const getDescendantIds = <T extends PropertyLike>(
  propertyId: string,
  properties: readonly T[] | Map<string, T>,
): Set<string> => {
  const list =
    properties instanceof Map ? Array.from(properties.values()) : (properties as readonly T[])
  const childrenByParent = new Map<string, T[]>()
  for (const p of list) {
    if (!p.parent_property_id) continue
    const bucket = childrenByParent.get(p.parent_property_id) ?? []
    bucket.push(p)
    childrenByParent.set(p.parent_property_id, bucket)
  }

  const out = new Set<string>()
  const visit = (parentId: string, depth: number): void => {
    if (depth > MAX_PROPERTY_NESTING_DEPTH) return
    const children = childrenByParent.get(parentId)
    if (!children) return
    for (const child of children) {
      if (out.has(child.id)) continue
      out.add(child.id)
      visit(child.id, depth + 1)
    }
  }
  visit(propertyId, 1)
  return out
}

/**
 * Orders properties into a depth-first "tree order": each parent is immediately
 * followed by its descendants, with siblings ordered by the provided
 * `siblingSort` (default: preserve input order). This is the order used by
 * the class editor to display nested properties under their parent.
 *
 * The returned list excludes any orphan properties whose `parent_property_id`
 * references a property that does not exist in the given collection.
 */
export const sortPropertiesTree = <T extends PropertyLike>(
  properties: readonly T[],
  siblingSort: (a: T, b: T) => number = () => 0,
): T[] => {
  const childrenByParent = new Map<string | null, T[]>()
  const ids = new Set<string>()
  for (const p of properties) ids.add(p.id)

  for (const p of properties) {
    const parentId = p.parent_property_id ?? null
    // Treat unknown parent refs as orphans: skip them from the output.
    if (parentId !== null && !ids.has(parentId)) continue
    const bucket = childrenByParent.get(parentId) ?? []
    bucket.push(p)
    childrenByParent.set(parentId, bucket)
  }

  for (const bucket of childrenByParent.values()) bucket.sort(siblingSort)

  const out: T[] = []
  // `depth` is the 0-indexed recursion depth, matching the backend's
  // `compute_depth`: depth=0 visits top-level properties, depth=20 visits the
  // deepest children the backend will accept. We bail only once we've moved
  // past the deepest allowed level.
  const visit = (parentId: string | null, depth: number): void => {
    if (depth > MAX_PROPERTY_NESTING_DEPTH) return
    const children = childrenByParent.get(parentId)
    if (!children) return
    for (const child of children) {
      out.push(child)
      visit(child.id, depth + 1)
    }
  }
  visit(null, 0)
  return out
}

/**
 * Convenience helper for building a `PropertyValueLookup` from an array of
 * `{ id, values }` entries (the shape returned by the item-level properties
 * endpoint).
 *
 * Audio values are dropped at this boundary: audio properties are not
 * nestable, so their values can never satisfy a trigger condition.
 */
export const toValueLookup = (
  entries: readonly {
    id: string
    values: readonly (string | TeamPropertyTextValue | TeamPropertyAudioValue)[]
  }[],
): PropertyValueLookup => {
  const out: PropertyValueLookup = {}
  for (const e of entries) {
    out[e.id] = e.values.filter(
      (v): v is string | TeamPropertyTextValue => !isTeamPropertyAudioValue(v),
    )
  }
  return out
}
